import { useCallback, useMemo, useState } from 'react';
import type { CatalogFilterDraft } from '../../components/CatalogFilterSheetContent';
import {
  countActiveCatalogFilters,
  DEFAULT_CATALOG_FILTERS,
  hasActiveCatalogFilters,
} from '../../domain/catalogFilterOptions';
import { FilterSheetButton } from '../../../shared/filters/FilterSheetButton';

type FilterSheetVm = {
  itemTypeFilter: typeof DEFAULT_CATALOG_FILTERS.itemTypeFilter;
  statusFilter: typeof DEFAULT_CATALOG_FILTERS.statusFilter;
  categoryFilter: typeof DEFAULT_CATALOG_FILTERS.categoryFilter;
  catalogBrandFilter: typeof DEFAULT_CATALOG_FILTERS.brandFilter;
  catalogSupplierFilter: typeof DEFAULT_CATALOG_FILTERS.supplierFilter;
  alertFilter: typeof DEFAULT_CATALOG_FILTERS.alertFilter;
  incompleteFilter: typeof DEFAULT_CATALOG_FILTERS.incompleteFilter;
  setItemTypeFilter: (v: typeof DEFAULT_CATALOG_FILTERS.itemTypeFilter) => void;
  setStatusFilter: (v: typeof DEFAULT_CATALOG_FILTERS.statusFilter) => void;
  setCategoryFilter: (v: typeof DEFAULT_CATALOG_FILTERS.categoryFilter) => void;
  setCatalogBrandFilter: (v: typeof DEFAULT_CATALOG_FILTERS.brandFilter) => void;
  setCatalogSupplierFilter: (v: typeof DEFAULT_CATALOG_FILTERS.supplierFilter) => void;
  setAlertFilter: (v: typeof DEFAULT_CATALOG_FILTERS.alertFilter) => void;
  setIncompleteFilter: (v: typeof DEFAULT_CATALOG_FILTERS.incompleteFilter) => void;
};

/**
 * Estado del sheet de filtros: draft, open/close, apply, clear.
 * Extrae ~95 líneas de CatalogScreen a un hook reutilizable.
 */
export function useCatalogFilterSheet(vm: FilterSheetVm, _isWide: boolean) {
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<CatalogFilterDraft | null>(null);

  // El orden vive en la toolbar de la lista (control visible propio): no es un
  // filtro que reduce resultados, así que no entra al badge del botón Filtros.
  const activeFilterCount = useMemo(
    () =>
      countActiveCatalogFilters({
        itemTypeFilter: vm.itemTypeFilter,
        statusFilter: vm.statusFilter,
        categoryFilter: vm.categoryFilter,
        sortBy: DEFAULT_CATALOG_FILTERS.sortBy,
        brandFilter: vm.catalogBrandFilter,
        supplierFilter: vm.catalogSupplierFilter,
        alertFilter: vm.alertFilter,
        incompleteFilter: vm.incompleteFilter,
      }),
    [
      vm.itemTypeFilter,
      vm.statusFilter,
      vm.categoryFilter,
      vm.catalogBrandFilter,
      vm.catalogSupplierFilter,
      vm.alertFilter,
      vm.incompleteFilter,
    ],
  );

  const openFilterSheet = useCallback(() => {
    setFilterDraft({
      itemTypeFilter: vm.itemTypeFilter,
      statusFilter: vm.statusFilter,
      categoryFilter: vm.categoryFilter,
      brandFilter: vm.catalogBrandFilter,
      supplierFilter: vm.catalogSupplierFilter,
      alertFilter: vm.alertFilter,
      incompleteFilter: vm.incompleteFilter,
    });
    setFilterSheetOpen(true);
  }, [
    vm.itemTypeFilter,
    vm.statusFilter,
    vm.categoryFilter,
    vm.catalogBrandFilter,
    vm.catalogSupplierFilter,
    vm.alertFilter,
    vm.incompleteFilter,
  ]);

  const applyFilterDraft = useCallback(() => {
    if (!filterDraft) return;
    if (filterDraft.itemTypeFilter !== vm.itemTypeFilter) vm.setItemTypeFilter(filterDraft.itemTypeFilter);
    if (filterDraft.statusFilter !== vm.statusFilter) vm.setStatusFilter(filterDraft.statusFilter);
    if (filterDraft.categoryFilter !== vm.categoryFilter) vm.setCategoryFilter(filterDraft.categoryFilter);
    if (filterDraft.brandFilter !== vm.catalogBrandFilter) vm.setCatalogBrandFilter(filterDraft.brandFilter);
    if (filterDraft.supplierFilter !== vm.catalogSupplierFilter) vm.setCatalogSupplierFilter(filterDraft.supplierFilter);
    if (filterDraft.alertFilter !== vm.alertFilter) vm.setAlertFilter(filterDraft.alertFilter);
    if (filterDraft.incompleteFilter !== vm.incompleteFilter) vm.setIncompleteFilter(filterDraft.incompleteFilter);
    setFilterSheetOpen(false);
  }, [
    filterDraft,
    vm.itemTypeFilter,
    vm.statusFilter,
    vm.categoryFilter,
    vm.catalogBrandFilter,
    vm.catalogSupplierFilter,
    vm.alertFilter,
    vm.incompleteFilter,
    vm.setItemTypeFilter,
    vm.setStatusFilter,
    vm.setCategoryFilter,
    vm.setCatalogBrandFilter,
    vm.setCatalogSupplierFilter,
    vm.setAlertFilter,
    vm.setIncompleteFilter,
  ]);

  const clearFilterDraft = useCallback(() => {
    setFilterDraft({
      itemTypeFilter: DEFAULT_CATALOG_FILTERS.itemTypeFilter,
      statusFilter: DEFAULT_CATALOG_FILTERS.statusFilter,
      categoryFilter: DEFAULT_CATALOG_FILTERS.categoryFilter,
      brandFilter: DEFAULT_CATALOG_FILTERS.brandFilter,
      supplierFilter: DEFAULT_CATALOG_FILTERS.supplierFilter,
      alertFilter: DEFAULT_CATALOG_FILTERS.alertFilter,
      incompleteFilter: DEFAULT_CATALOG_FILTERS.incompleteFilter,
    });
  }, []);

  const filterTrigger = useMemo(
    () => (
      <FilterSheetButton
        activeCount={activeFilterCount}
        onOpen={openFilterSheet}
        /** Compact Square (proto): misma píldora en mobile y desk. */
        variant="desk"
        testID="catalog.filters.open"
      />
    ),
    [activeFilterCount, openFilterSheet],
  );

  const hasActiveDraft =
    filterDraft != null &&
    hasActiveCatalogFilters({ ...filterDraft, sortBy: DEFAULT_CATALOG_FILTERS.sortBy });

  return {
    filterSheetOpen,
    setFilterSheetOpen,
    filterDraft,
    setFilterDraft,
    applyFilterDraft,
    clearFilterDraft,
    filterTrigger,
    activeFilterCount,
    hasActiveDraft,
  };
}
